import type React from 'react';
import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRight, ChevronDown, File, Folder, FolderClosed, FolderRoot, Files, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import Spinner from '@/components/shared/Spinner';
import { AnalysisSetBadge } from '@/components/deep-analysis/AnalysisSetBadge';
import { FolderActionsDropdown } from '@/components/folders/FolderActionsDropdown';
import { useFolderDropZone } from '@/hooks/useFolderDropZone';
import type { FolderTree as FolderTreeType } from '@/hooks/useFolders';
import { cn } from '@/lib/utils';
import { isCorrespondenceFolder as checkIsCorrespondenceFolder } from '@/utils/document-utils';
import * as Tooltip from '@radix-ui/react-tooltip';

interface FolderTreeProps {
  folders: FolderTreeType[];
  /** null while the count is still loading */
  totalDocuments: number | null;
  matterSlug: string;
  onRefresh: () => void;
  onFilesDropped?: (files: File[], folderId: number) => void;
  /** Navigation mode: 'router' navigates to folder pages, 'callback' calls onFolderSelect */
  navigationMode?: 'router' | 'callback';
  /** Called when a folder is selected in callback mode. null = All Files */
  onFolderSelect?: (folder: FolderTreeType | null) => void;
  /** GUID of the currently selected folder for visual highlighting (callback mode) */
  selectedFolderGuid?: string | null;
  /** Whether to show folder action dropdown menus. Default: true */
  showFolderActions?: boolean;
  /** Whether to hide the "All Files" row. Default: false */
  hideAllFiles?: boolean;
  /** Label for the root row. Default: All Files */
  allFilesLabel?: string;
  /** Put the All Files / Root row inside the folder list and indent children. Sync destination/preview only. Default: false */
  allFilesInFolderList?: boolean;
  /** Whether to disable the "All Files" option. Default: false */
  disableAllFiles?: boolean;
  /** Optional callback to determine if a folder should be disabled for selection. Receives folder and returns true to disable. */
  shouldDisableFolder?: (folder: FolderTreeType) => boolean;
  /** Optional callback to determine if a folder can be expanded even when disabled. Default: false (disabled folders can't expand) */
  allowExpandWhenDisabled?: boolean;
  /** Omit folders from the tree (e.g. auto-included Client Documents subtree). */
  shouldHideFolder?: (folder: FolderTreeType) => boolean;
  /** Folder GUIDs shown as checked when `onFolderToggleSelection` is set. */
  selectedFolderGuidsForChecked?: Set<string>;
  /** Toggle folder-level selection (checkbox click). Row click still navigates. */
  onFolderToggleSelection?: (folder: FolderTreeType) => void;
  /** Whether Deep Analysis badges/actions should be shown. Default: false */
  showDeepAnalysis?: boolean;
  /** Preserve the Deep Analysis-only route view when navigating from this tree. */
  deepAnalysisOnly?: boolean;
  /** Expand every folder that has children on first render. */
  defaultExpandAll?: boolean;
  /** Extra content after the folder name (for example an iManage source chip). */
  renderFolderAccessory?: (folder: FolderTreeType) => React.ReactNode;
  /** Style this row as a preview placeholder rather than a real folder. */
  isPlaceholderFolder?: (folder: FolderTreeType) => boolean;
  /** Style this row as a file in the create-sync preview. */
  isPreviewFile?: (folder: FolderTreeType) => boolean;
  /** Show document-count badges. Default: true */
  showDocumentCounts?: boolean;
  /** When false, rows stay highlighted but clicks do not change the selection. Default: true */
  selectionEnabled?: boolean;
  /** Vertical padding class for folder rows. Default matches the shared tree. */
  rowClassName?: string;
}

function filterHiddenFolders(
  folders: FolderTreeType[],
  shouldHideFolder?: (folder: FolderTreeType) => boolean,
): FolderTreeType[] {
  if (!shouldHideFolder) return folders;
  return folders
    .filter((folder) => !shouldHideFolder(folder))
    .map((folder) => ({
      ...folder,
      children: folder.children
        ? filterHiddenFolders(folder.children, shouldHideFolder)
        : [],
    }));
}

interface FolderTreeNodeProps {
  folder: FolderTreeType;
  matterSlug: string;
  onRefresh: () => void;
  level: number;
  onFilesDropped?: (files: File[], folderId: number) => void;
  navigationMode?: 'router' | 'callback';
  onFolderSelect?: (folder: FolderTreeType | null) => void;
  selectedFolderGuid?: string | null;
  showFolderActions?: boolean;
  shouldDisableFolder?: (folder: FolderTreeType) => boolean;
  allowExpandWhenDisabled?: boolean;
  selectedFolderGuidsForChecked?: Set<string>;
  onFolderToggleSelection?: (folder: FolderTreeType) => void;
  showDeepAnalysis?: boolean;
  deepAnalysisOnly?: boolean;
  defaultExpandAll?: boolean;
  renderFolderAccessory?: (folder: FolderTreeType) => React.ReactNode;
  isPlaceholderFolder?: (folder: FolderTreeType) => boolean;
  isPreviewFile?: (folder: FolderTreeType) => boolean;
  showDocumentCounts?: boolean;
  selectionEnabled?: boolean;
  rowClassName?: string;
}

const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
  folder,
  matterSlug,
  onRefresh,
  level,
  onFilesDropped,
  navigationMode = 'router',
  onFolderSelect,
  selectedFolderGuid,
  showFolderActions = true,
  shouldDisableFolder,
  allowExpandWhenDisabled = false,
  selectedFolderGuidsForChecked,
  onFolderToggleSelection,
  showDeepAnalysis = false,
  deepAnalysisOnly = false,
  defaultExpandAll = false,
  renderFolderAccessory,
  isPlaceholderFolder,
  isPreviewFile,
  showDocumentCounts = true,
  selectionEnabled = true,
  rowClassName = 'py-2.5',
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpandAll);
  const isPlaceholder = Boolean(isPlaceholderFolder?.(folder));
  const isFileRow = Boolean(isPreviewFile?.(folder));

  const isSelected = selectedFolderGuid === folder.guid;
  const isFolderChecked = selectedFolderGuidsForChecked?.has(folder.guid) ?? false;
  const showFolderCheckbox = Boolean(onFolderToggleSelection);
  const isSyncedFolder = folder.is_synced === true;
  const isSyncRoot = folder.is_sync_root === true;
  const isCorrespondence = checkIsCorrespondenceFolder(folder);
  const analysisSetTooltip = folder.analysis_set_state === 'included'
    ? 'This folder is included in Deep Analysis. Files in it will be processed unless explicitly excluded.'
    : 'This folder is included in Deep Analysis through a parent folder.';

  // Disable folder if shouldDisableFolder callback returns true
  const isDisabled = shouldDisableFolder ? shouldDisableFolder(folder) : false;
  // Show indicator for both synced folders and sync roots
  const showSyncIndicator = isSyncedFolder || isSyncRoot;

  const hasChildren = !isPlaceholder && folder.children && folder.children.length > 0;

  // Auto-expand handler for drag hover
  const handleDragHover = () => {
    if (hasChildren && !isExpanded && (!isDisabled || allowExpandWhenDisabled)) {
      setIsExpanded(true);
    }
  };

  // Drag and drop functionality
  const { isDraggingOver, dragHandlers } = useFolderDropZone({
    folderId: folder.folder_id || 0,
    folderName: folder.name,
    onFilesDropped: onFilesDropped || (() => {}),
    disabled: !onFilesDropped || !folder.folder_id,
    onDragHover: hasChildren ? handleDragHover : undefined,
    autoExpandDelay: 400
  });

  const handleFolderClick = () => {
    // Don't allow clicks on disabled folders
    if (isDisabled || isPlaceholder || !selectionEnabled) {
      return;
    }

    if (navigationMode === 'callback' && onFolderSelect) {
      onFolderSelect(folder);
    } else if (isCorrespondence) {
      navigate({
        to: '/matter/$slug/case-files/correspondence',
        params: { slug: matterSlug },
        search: deepAnalysisOnly ? { view: 'deep-analysis' } : {},
      });
    } else {
      navigate({
        to: '/matter/$slug/case-files/folder/$folderGuid',
        params: { slug: matterSlug, folderGuid: folder.guid },
        search: deepAnalysisOnly ? { view: 'deep-analysis' } : {},
      });
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Don't allow expansion of disabled folders unless allowExpandWhenDisabled is true
    if (isDisabled && !allowExpandWhenDisabled) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  // Indentation based on nesting level
  const paddingLeft = level * 14 + 8;

  return (
    <div className="select-none">
      {/* Folder Row */}
      <div
        {...dragHandlers}
        className={cn(
          "group flex items-center px-3 transition-all duration-150 border-b border-gray-100",
          rowClassName,
          isDisabled
            ? "cursor-not-allowed bg-gray-50 opacity-50"
            : (selectionEnabled && !isPlaceholder) ? "cursor-pointer" : "cursor-default",
          !isDisabled && isDraggingOver
            ? "ring-2 ring-inset ring-blue-500 bg-blue-50 border-blue-400"
            : !isDisabled && isSelected
              ? "bg-blue-50 border-blue-200"
              : !isDisabled && selectionEnabled && !isPlaceholder && "hover:bg-gray-50"
        )}
        onClick={handleFolderClick}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {showFolderCheckbox ? (
          <div
            className="flex items-center justify-center mr-1.5"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isFolderChecked}
              onCheckedChange={() => {
                if (!isDisabled) onFolderToggleSelection?.(folder);
              }}
              disabled={isDisabled}
              className="h-4 w-4"
              aria-label={`Select folder ${folder.name}`}
            />
          </div>
        ) : null}
        {/* Expand/Collapse Button */}
        <div className="flex items-center justify-center w-5 h-5 mr-1.5">
          {hasChildren ? (
            <button
              type="button"
              onClick={handleExpandClick}
              className={cn(
                "flex items-center justify-center w-5 h-5 rounded-xs transition-colors duration-150",
                (isDisabled && !allowExpandWhenDisabled) ? "cursor-not-allowed" : "hover:bg-gray-200"
              )}
              title={(isDisabled && !allowExpandWhenDisabled) ? 'Cannot expand synced folder' : isExpanded ? 'Collapse folder' : 'Expand folder'}
              disabled={isDisabled && !allowExpandWhenDisabled}
            >
              {isExpanded ? (
                <ChevronDown className={cn("h-4 w-4", isDisabled ? "text-gray-400" : "text-gray-600")} />
              ) : (
                <ChevronRight className={cn("h-4 w-4", isDisabled ? "text-gray-400" : "text-gray-600")} />
              )}
            </button>
          ) : (
            <div className="w-5 h-5" /> // Empty space for alignment
          )}
        </div>

        {/* Folder Icon */}
        {isPlaceholder ? (
          <div className="mr-3 h-5 w-5 shrink-0" />
        ) : (
        <div className="relative mr-3 flex h-5 w-5 items-center justify-center">
          {isFileRow ? (
            <File className="h-5 w-5 text-gray-500" />
          ) : (
          <Folder className={cn(
            "h-5 w-5 transition-colors",
            isDraggingOver
              ? "text-amber-500/50 fill-amber-400/20"
              : "text-amber-500/40 fill-amber-400/15"
          )} />
          )}
          {showDeepAnalysis && folder.analysis_set_effective && (
            <span
              className="absolute -right-1 -top-1"
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <AnalysisSetBadge
                active={folder.analysis_set_effective}
                compact
                iconOnly
                tooltip={analysisSetTooltip}
              />
            </span>
          )}
        </div>
        )}

        {/* Folder Name */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span
            className={cn(
              "font-medium text-sm truncate block",
              isPlaceholder
                ? "text-gray-500"
                : "text-gray-700 hover:text-primary-500 transition-colors duration-150",
            )}
            title={folder.name}
          >
            {folder.name}
          </span>
          {renderFolderAccessory?.(folder)}
          {/* Sync indicator for synced folders and sync roots */}
          {showSyncIndicator && (() => {
            const providers = folder.sync_providers || [];
            const providerLabels = [
              { key: 'imanage', label: 'iManage' },
              { key: 'outlook', label: 'Outlook' },
              { key: 'onedrive', label: 'OneDrive' },
              { key: 'sharepoint', label: 'SharePoint' },
            ];
            const syncedProviderLabels = providerLabels
              .filter(({ key }) => providers.includes(key))
              // Intentionally show only the first two providers to keep folder rows compact.
              .slice(0, 2)
              .map(({ label }) => label);
            const message = syncedProviderLabels.length > 0
              ? `This folder is synced from ${syncedProviderLabels.join(' and ')}`
              : 'This folder is synced';

            return (
              <Tooltip.Provider>
                <Tooltip.Root delayDuration={300}>
                  <Tooltip.Trigger asChild>
                    <RefreshCw className="h-3.5 w-3.5 text-blue-400/80 shrink-0" />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg"
                      sideOffset={5}
                    >
                      {message}
                      <Tooltip.Arrow className="fill-gray-900" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            );
          })()}
        </div>

        {/* Document Count Badge */}
        {showDocumentCounts && (
        <div className={showFolderActions ? "mr-3" : "mr-0"}>
          <Badge variant="outline" className="font-normal text-xs">
            {folder.document_count ?? 0}
          </Badge>
        </div>
        )}

        {/* Actions Dropdown */}
        {showFolderActions && folder.guid && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="opacity-100"
          >
            <FolderActionsDropdown
              matterSlug={matterSlug}
              folder={folder}
              onRefresh={onRefresh}
              deepAnalysisEnabled={showDeepAnalysis}
            />
          </div>
        )}
      </div>

      {/* Children (Subfolders) */}
      {hasChildren && isExpanded && (
        <div className="ml-0">
          {folder.children?.map((childFolder) => (
            <FolderTreeNode
              key={childFolder.guid}
              folder={childFolder}
              matterSlug={matterSlug}
              onRefresh={onRefresh}
              level={level + 1}
              onFilesDropped={onFilesDropped}
              navigationMode={navigationMode}
              onFolderSelect={onFolderSelect}
              selectedFolderGuid={selectedFolderGuid}
              showFolderActions={showFolderActions}
              shouldDisableFolder={shouldDisableFolder}
              allowExpandWhenDisabled={allowExpandWhenDisabled}
              selectedFolderGuidsForChecked={selectedFolderGuidsForChecked}
              onFolderToggleSelection={onFolderToggleSelection}
              showDeepAnalysis={showDeepAnalysis}
              deepAnalysisOnly={deepAnalysisOnly}
              defaultExpandAll={defaultExpandAll}
              renderFolderAccessory={renderFolderAccessory}
              isPlaceholderFolder={isPlaceholderFolder}
              isPreviewFile={isPreviewFile}
              showDocumentCounts={showDocumentCounts}
              selectionEnabled={selectionEnabled}
              rowClassName={rowClassName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  totalDocuments,
  matterSlug,
  onRefresh,
  onFilesDropped,
  navigationMode = 'router',
  onFolderSelect,
  selectedFolderGuid,
  showFolderActions = true,
  hideAllFiles = false,
  allFilesLabel = 'All Files',
  allFilesInFolderList = false,
  disableAllFiles = false,
  shouldDisableFolder,
  allowExpandWhenDisabled = false,
  shouldHideFolder,
  selectedFolderGuidsForChecked,
  onFolderToggleSelection,
  showDeepAnalysis = false,
  deepAnalysisOnly = false,
  defaultExpandAll = false,
  renderFolderAccessory,
  isPlaceholderFolder,
  isPreviewFile,
  showDocumentCounts = true,
  selectionEnabled = true,
  rowClassName = 'py-2.5',
}) => {
  const navigate = useNavigate();

  const isAllFilesSelected = selectedFolderGuid === null;
  const totalDocumentsLabel = totalDocuments ?? <Spinner size="xs" />;

  const handleAllFilesClick = () => {
    if (!selectionEnabled) {
      return;
    }
    if (navigationMode === 'callback' && onFolderSelect) {
      onFolderSelect(null);
    } else {
      navigate({
        to: '/matter/$slug/case-files/all-files',
        params: { slug: matterSlug },
        search: deepAnalysisOnly ? { view: 'deep-analysis' } : {},
      });
    }
  };

  // Build a hierarchical tree structure from flat folder list
  // If folders already have children populated (hierarchical data), use them directly
  const folderTree = useMemo(() => {
    // Check if data is already hierarchical (has populated children arrays)
    const isAlreadyHierarchical = folders.some(folder =>
      folder.children && folder.children.length > 0
    );

    if (isAlreadyHierarchical) {
      // Data is already hierarchical - only return root folders (no parent)
      return folders.filter(folder => !folder.parent_folder_id);
    }

    // Otherwise, build tree from flat folder list
    const folderMap = new Map<number, FolderTreeType>();
    const rootFolders: FolderTreeType[] = [];

    // First pass: create folder objects with children arrays (use folder_id as key for parent lookup)
    folders.forEach(folder => {
      folderMap.set(folder.folder_id, { ...folder, children: [] });
    });

    // Second pass: build parent-child relationships
    folders.forEach(folder => {
      const folderObj = folderMap.get(folder.folder_id);

      if (folderObj && folder.parent_folder_id && folderMap.has(folder.parent_folder_id)) {
        // This folder has a parent, add it to parent's children
        const parent = folderMap.get(folder.parent_folder_id);
        if (parent) {
          parent.children.push(folderObj);
        }
      } else if (folderObj) {
        // This is a root folder (parent_folder_id is null/undefined)
        rootFolders.push(folderObj);
      }
    });

    return rootFolders;
  }, [folders]);

  const visibleFolderTree = useMemo(
    () => filterHiddenFolders(folderTree, shouldHideFolder),
    [folderTree, shouldHideFolder],
  );

  // Check if all folders are synced (indicates a root-level iManage sync exists)
  // When there's a root-level sync, ALL folders should be disabled
  const allFoldersSynced = useMemo(() => {
    if (folders.length === 0) return false;
    return folders.every(folder => folder.is_synced === true);
  }, [folders]);

  // Disable "All Files" when:
  // 1. disableAllFiles prop is true (e.g., existing iManage folder syncs), OR
  // 2. shouldDisableFolder is provided (picker mode) AND all folders are synced (root-level sync)
  const isAllFilesDisabled = disableAllFiles || (shouldDisableFolder && allFoldersSynced);

  const allFilesButton = (inFolderList: boolean) => {
    const selectedClasses = inFolderList
      ? 'bg-blue-50 border-blue-200'
      : 'bg-primary-100 border-primary-200'
    const idleClasses = inFolderList
      ? 'hover:bg-gray-50'
      : 'bg-slate-50 hover:bg-slate-100 border-slate-200'
    const buttonClassName = cn(
      'group flex items-center transition-colors duration-150',
      rowClassName,
      selectionEnabled ? 'cursor-pointer' : 'cursor-default',
      inFolderList
        ? 'border-b border-gray-100 px-3'
        : 'mb-2 rounded-lg border',
      isAllFilesSelected ? selectedClasses : idleClasses,
    )

    return (
      <div
        className={buttonClassName}
        style={{ paddingLeft: '8px', paddingRight: '12px' }}
        onClick={handleAllFilesClick}
      >
        <div className="flex items-center justify-center w-5 h-5 mr-1.5" />
        <div className="mr-3">
          {inFolderList ? (
            <FolderRoot className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
          ) : (
            <Files className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-sm text-gray-900 hover:text-primary-500 transition-colors duration-150">
            {allFilesLabel}
          </span>
        </div>
        {showDocumentCounts && (
        <div className={showFolderActions ? 'mr-3' : 'mr-0'}>
          <Badge variant="outline" className="font-normal text-xs bg-primary-50 border-primary-200">
            {totalDocumentsLabel}
          </Badge>
        </div>
        )}
        {showFolderActions && <div className="w-8" />}
      </div>
    )
  }


  return (
    <div className="space-y-1">
      {/* All Files Row */}
      {!hideAllFiles && !allFilesInFolderList && (
        isAllFilesDisabled ? (
          <Tooltip.Provider>
            <Tooltip.Root delayDuration={300}>
              <Tooltip.Trigger asChild>
                <div
                  className={cn(
                    "group flex items-center mb-2 rounded-lg transition-colors duration-150 border",
                    rowClassName,
                    "bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed"
                  )}
                  style={{ paddingLeft: '8px', paddingRight: '12px' }}
                >
                  <div className="flex items-center justify-center w-5 h-5 mr-1.5">
                    {/* Empty space for alignment with folder expand buttons */}
                  </div>

                  <div className="mr-3">
                    <Files className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm text-gray-900">
                      {allFilesLabel}
                    </span>
                  </div>

                  <div className={showFolderActions ? "mr-3" : "mr-0"}>
                    <Badge variant="outline" className="font-normal text-xs bg-primary-50 border-primary-200">
                      {totalDocumentsLabel}
                    </Badge>
                  </div>

                  {showFolderActions && <div className="w-8"> {/* Space for alignment with folder actions */}</div>}
                </div>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded-sm shadow-lg"
                  sideOffset={5}
                >
                  {disableAllFiles
                    ? `Cannot sync to ${allFilesLabel} - folder-level iManage syncs already exist`
                    : 'Cannot sync here - root-level iManage sync already exists'}
                  <Tooltip.Arrow className="fill-gray-900" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        ) : (
          allFilesButton(false)
        )
      )}

      {/* Folder Tree */}
      <div className="bg-white rounded-lg border border-gray-200">
        {!hideAllFiles && allFilesInFolderList && (
          isAllFilesDisabled ? (
            <div
              className="group flex items-center py-2.5 border-b border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
              style={{ paddingLeft: '8px', paddingRight: '12px' }}
            >
              <div className="flex items-center justify-center w-5 h-5 mr-1.5" />
              <div className="mr-3">
                <FolderRoot className="h-5 w-5 text-amber-500/40 fill-amber-400/15" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm text-gray-900">
                  {allFilesLabel}
                </span>
              </div>
              {showDocumentCounts && (
                <Badge variant="outline" className="font-normal text-xs bg-primary-50 border-primary-200">
                  {totalDocumentsLabel}
                </Badge>
              )}
            </div>
          ) : (
            allFilesButton(true)
          )
        )}
        {visibleFolderTree.length > 0 ? (
          <div>
            {visibleFolderTree.map((folder) => (
              <FolderTreeNode
                key={folder.guid}
                folder={folder}
                matterSlug={matterSlug}
                onRefresh={onRefresh}
                level={allFilesInFolderList && !hideAllFiles ? 1 : 0}
                onFilesDropped={onFilesDropped}
                navigationMode={navigationMode}
                onFolderSelect={onFolderSelect}
                selectedFolderGuid={selectedFolderGuid}
                showFolderActions={showFolderActions}
                shouldDisableFolder={shouldDisableFolder}
                allowExpandWhenDisabled={allowExpandWhenDisabled}
                selectedFolderGuidsForChecked={selectedFolderGuidsForChecked}
                onFolderToggleSelection={onFolderToggleSelection}
                showDeepAnalysis={showDeepAnalysis}
                deepAnalysisOnly={deepAnalysisOnly}
                defaultExpandAll={defaultExpandAll}
                renderFolderAccessory={renderFolderAccessory}
                isPlaceholderFolder={isPlaceholderFolder}
                isPreviewFile={isPreviewFile}
                showDocumentCounts={showDocumentCounts}
                selectionEnabled={selectionEnabled}
                rowClassName={rowClassName}
              />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <FolderClosed className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No folders found</p>
          </div>
        )}
      </div>
    </div>
  );
};
